import { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  NumberInput,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconInfoCircle, IconPencil, IconRefresh } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import type { ApiError} from '../../api/client';
import { api, unwrap } from '../../api/client';
import type { CounterDefinition, CounterReading } from '../../api/types';
import { useSession } from '../../app/session';
import { DataTable } from '../../components/DataTable';
import { formatCounter, formatDateTime } from '../../lib/format';
import { notifyError, notifySaved } from '../../lib/notify';
import { useAircraft } from './AircraftDetailLayout';

export function AircraftCountersTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const aircraft = useAircraft();
  const { can } = useSession();
  const [adjusting, setAdjusting] = useState<CounterDefinition | null>(null);

  const counters = useQuery({
    queryKey: ['aircraft', aircraft.registration, 'counters'],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/aircraft/{registration}/counters/', {
          params: { path: { registration: aircraft.registration } },
        }),
      ),
  });

  const history = useQuery({
    queryKey: ['aircraft', aircraft.registration, 'counter-history'],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/aircraft/{registration}/counter-history/', {
          params: { path: { registration: aircraft.registration } },
        }),
      ),
  });

  const recompute = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST('/api/v1/aircraft/{registration}/recompute-counters/', {
          params: { path: { registration: aircraft.registration } },
        }),
      ),
    onSuccess: async () => {
      notifySaved(t('action.recompute'));
      await queryClient.invalidateQueries({ queryKey: ['aircraft', aircraft.registration] });
    },
    onError: (error: ApiError) => notifyError(error),
  });

  const columns = useMemo<ColumnDef<CounterReading, unknown>[]>(
    () => [
      {
        accessorKey: 'created_at',
        header: t('common.date'),
        meta: { width: 150 },
        cell: ({ row }) => formatDateTime(row.original.created_at),
      },
      { accessorKey: 'counter_label', header: t('aircraft.counters') },
      {
        accessorKey: 'previous_value',
        header: t('logbook.broughtForward'),
        meta: { align: 'right', mono: true, width: 110 },
        cell: ({ row }) => formatCounter(row.original.previous_value, row.original.unit),
      },
      {
        accessorKey: 'value',
        header: t('aircraft.currentValue'),
        meta: { align: 'right', mono: true, width: 110 },
        cell: ({ row }) => formatCounter(row.original.value, row.original.unit),
      },
      {
        accessorKey: 'delta',
        header: t('logbook.counterMovements'),
        meta: { align: 'right', mono: true, width: 100 },
        cell: ({ row }) => {
          const delta = Number(row.original.delta);
          return (
            <Text fz="sm" c={delta < 0 ? 'red' : undefined} ff="monospace">
              {delta > 0 ? '+' : ''}
              {formatCounter(row.original.delta, row.original.unit)}
            </Text>
          );
        },
      },
      {
        accessorKey: 'reason',
        header: t('maintenance.area'),
        cell: ({ row }) => (
          <Group gap={6} wrap="nowrap">
            <Text fz="sm">{row.original.reason_label}</Text>
            {row.original.is_adjustment ? (
              <Badge size="xs" color="grape">
                {t('action.adjust')}
              </Badge>
            ) : null}
          </Group>
        ),
      },
      {
        accessorKey: 'comment',
        header: t('common.notes'),
        cell: ({ row }) => (
          <Text fz="xs" c="dimmed" lineClamp={1}>
            {row.original.comment || row.original.recorded_by_name}
          </Text>
        ),
      },
    ],
    [t],
  );

  const canAdjust = can('aircraft.manage_counters');

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
        {(counters.data ?? []).map((counter) => (
          <Card key={counter.id} p="md">
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Stack gap={2}>
                <Text fz="xs" tt="uppercase" c="dimmed" fw={600}>
                  {counter.label}
                </Text>
                <Text fz={26} fw={700} lh={1.1} className="identifier">
                  {formatCounter(counter.current_value, counter.unit)}
                </Text>
                <Text fz="xs" c="dimmed">
                  {t('aircraft.initialValue')}: {formatCounter(counter.initial_value, counter.unit)}
                  {counter.source === 'MANUAL' ? ` · ${t('action.edit')}` : ''}
                </Text>
              </Stack>
              {canAdjust ? (
                <Button
                  size="compact-sm"
                  variant="light"
                  leftSection={<IconPencil size={14} />}
                  onClick={() => setAdjusting(counter)}
                >
                  {t('action.adjust')}
                </Button>
              ) : null}
            </Group>
          </Card>
        ))}
      </SimpleGrid>

      {canAdjust ? (
        <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
          <Group justify="space-between" wrap="wrap" gap="sm">
            <Text fz="sm">{t('aircraft.adjustCounterHint')}</Text>
            <Button
              size="compact-sm"
              variant="light"
              leftSection={<IconRefresh size={14} />}
              loading={recompute.isPending}
              onClick={() => recompute.mutate()}
            >
              {t('action.recompute')}
            </Button>
          </Group>
        </Alert>
      ) : null}

      <div>
        <Title order={3} mb="sm">
          {t('aircraft.counterHistory')}
        </Title>
        <DataTable
          data={history.data ?? []}
          columns={columns}
          isLoading={history.isLoading}
          emptyTitle={t('aircraft.counterHistory')}
          minWidth={900}
        />
      </div>

      <AdjustCounterModal counter={adjusting} onClose={() => setAdjusting(null)} />
    </Stack>
  );
}

function AdjustCounterModal({
  counter,
  onClose,
}: {
  counter: CounterDefinition | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const form = useForm({
    initialValues: { value: '' as number | '', comment: '' },
    validate: {
      value: (value) => (value === '' ? t('common.requiredField') : null),
      comment: (value) => (value.trim() ? null : t('common.requiredField')),
    },
  });

  const adjust = useMutation({
    mutationFn: async (values: { value: number | ''; comment: string }) => {
      if (!counter) return null;
      return unwrap(
        await api.POST('/api/v1/counters/{id}/adjust/', {
          params: { path: { id: counter.id } },
          body: { value: String(values.value), comment: values.comment },
        }),
      );
    },
    onSuccess: async () => {
      notifySaved(t('action.adjust'));
      await queryClient.invalidateQueries({ queryKey: ['aircraft'] });
      await queryClient.invalidateQueries({ queryKey: ['alerts'] });
      form.reset();
      onClose();
    },
    onError: (error: ApiError) => notifyError(error),
  });

  return (
    <Modal
      opened={Boolean(counter)}
      onClose={onClose}
      title={`${t('aircraft.adjustCounter')} — ${counter?.label ?? ''}`}
    >
      <form onSubmit={form.onSubmit((values) => adjust.mutate(values))}>
        <Stack gap="md">
          <Text fz="sm" c="dimmed">
            {t('aircraft.adjustCounterHint')}
          </Text>
          <Group gap="xs">
            <Text fz="sm">{t('aircraft.currentValue')}:</Text>
            <Text fz="sm" fw={600} className="identifier">
              {formatCounter(counter?.current_value, counter?.unit)}
            </Text>
          </Group>
          <NumberInput
            label={t('aircraft.currentValue')}
            required
            decimalScale={counter?.unit === 'COUNT' ? 0 : 2}
            min={0}
            {...form.getInputProps('value')}
          />
          <Textarea
            label={t('common.notes')}
            required
            minRows={2}
            {...form.getInputProps('comment')}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              {t('action.cancel')}
            </Button>
            <Button type="submit" loading={adjust.isPending}>
              {t('action.save')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
